import { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { PasswordChecklist, passwordValid, generatePassword, usePasswordPolicy } from "../components/PasswordStrength";
import { CheckCircle, ArrowsClockwise } from "@phosphor-icons/react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const policy = usePasswordPolicy();
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== confirm) return toast.error("Passwords don't match.");
    if (!passwordValid(pw, policy)) return toast.error("Password doesn't meet the complexity rules.");
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Couldn't reset your password. Please request a new link.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block bg-[#071B3E]">
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <img src="/claro-shield.png?v=3" alt="Claro Compliance, LLC" className="h-14 w-auto object-contain" />
          <div className="font-display font-black text-2xl tracking-tight">CLARO <span className="text-white/50 font-medium text-base align-middle">Compliance</span></div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-card">
        <div className="w-full max-w-sm" data-testid="reset-page">
          <div className="overline text-muted-foreground mb-2">Password reset</div>
          <h1 className="font-display font-black text-3xl tracking-tighter mb-6">Choose a new password</h1>

          {!token ? (
            <div className="border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-3" data-testid="reset-no-token">
              This link is missing its reset token. Please use the link from your email, or request a new one.
              <div className="mt-3"><Link to="/login" className="underline">Back to sign in</Link></div>
            </div>
          ) : done ? (
            <div data-testid="reset-success" className="border border-green-300 bg-green-50 text-green-800 text-sm px-3 py-4">
              <div className="flex items-center gap-2 font-semibold"><CheckCircle size={18} weight="fill" />Password updated</div>
              <p className="mt-1">You can now sign in with your new password. Redirecting…</p>
              <Link to="/login" className="underline mt-2 inline-block">Go to sign in</Link>
            </div>
          ) : (
            <form onSubmit={submit}>
              {error && <div data-testid="reset-error" className="mb-4 border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="new-pw">New password</Label>
                    <button type="button" data-testid="reset-generate" onClick={() => { const g = generatePassword(Math.max(18, policy.min_length || 14)); setPw(g); setConfirm(g); }}
                      className="text-xs underline text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                      <ArrowsClockwise size={13} />Generate strong password
                    </button>
                  </div>
                  <Input id="new-pw" data-testid="reset-password-input" type="text" value={pw}
                    onChange={(e) => setPw(e.target.value)} className="rounded-none mt-1 font-mono" required />
                </div>
                <div>
                  <Label htmlFor="confirm-pw">Confirm new password</Label>
                  <Input id="confirm-pw" data-testid="reset-confirm-input" type="text" value={confirm}
                    onChange={(e) => setConfirm(e.target.value)} className="rounded-none mt-1 font-mono" required />
                </div>
                <PasswordChecklist pw={pw} />
                <Button type="submit" data-testid="reset-submit" disabled={loading || !passwordValid(pw, policy) || pw !== confirm}
                  className="w-full rounded-none bg-primary hover:bg-primary/90">{loading ? "Updating…" : "Update password"}</Button>
              </div>
              <Link to="/login" className="mt-6 inline-block text-sm underline text-muted-foreground hover:text-foreground">Back to sign in</Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
